import { type RootState, type AppDispatch } from "features/Application/globaltypes/redux";
import { connect, type ConnectedProps } from "react-redux";
import { type SendsProps } from "./types";
import { campaignsStateSelector } from "../state/selectors";
import GenericItemsView from "views/ItemsView/GenericItemsView";
import SendsNoResults from "views/library/campaigns/Sends/SendsNoResults";
import { CreateCampaignSendButton } from "./Sends/CreateCampaignSendButton";
import { useCallback, useEffect, useMemo } from "react";
import Restricted from "features/Application/Restricted";
import { RolePermissions, SortingDirection } from "enums";
import {
  fetchOverviewSends,
  completeCampaignSend,
  cancelCampaignSend,
} from "../state/thunks/campaignSendsOverviewThunk";
import { bindAction } from "interfaces";
import { setSelected } from "../state/slices/campaignSendsOverviewSlice";
import { Columns, ColumnToParamMap, getColumnOptions } from "./Sends/getColumnOptions";
import { userListUtils } from "utils";
import {
  CampaignRunCompletedSuccess,
  CampaignRunCanceledSuccess,
} from "features/Application/services/realTimeNotification/events/library/libraryEvents";
import { useHasPermissions } from "features/Application/hooks";

export type ScheduledSendsPropsAll = SendsProps & PropsFromRedux;

const rtnEvents = [CampaignRunCompletedSuccess, CampaignRunCanceledSuccess];

const Sends = (props: ScheduledSendsPropsAll) => {
  const {
    id,
    acceptHandlers,
    fetchCampaignSends,
    items,
    itemsCount,
    isLoading,
    isAllDataLoaded,
    selectedIds,
    setSelectedIds,
    completeCampaignRun,
    cancelCampaignSend,
    isSendDisabled,
  } = props;

  const hasManagePermission = useHasPermissions([RolePermissions.CampaignsManage]);

  useEffect(() => {
    acceptHandlers?.({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCompleteClick = useCallback(
    (id: number) => {
      completeCampaignRun(id);
    },
    [completeCampaignRun],
  );

  const handleCancelClick = useCallback(
    (id: number) => {
      cancelCampaignSend(id);
    },
    [cancelCampaignSend],
  );

  const columnOptions = useMemo(() => {
    return getColumnOptions({
      readonly: !hasManagePermission,
      handleCompleteClick,
      handleCancelClick,
    });
  }, [hasManagePermission, handleCompleteClick, handleCancelClick]);

  const handleFetch = useCallback(
    (
      skip: number = 0,
      top: number = 10,
      sortingColumnName: string = Columns.ScheduledTime.toLowerCase(),
      sortingDirection: SortingDirection = SortingDirection.Descending,
    ) => {
      const sortColumn = ColumnToParamMap[sortingColumnName as Lowercase<Columns>];
      const orderByParams = userListUtils.formatOrderParams(sortColumn, sortingDirection);
      fetchCampaignSends({ campaignId: id, skip, top, orderBy: orderByParams });
    },
    [id, fetchCampaignSends],
  );

  return (
    <Restricted
      permissions={[RolePermissions.CampaignsManage]}
      renderContent={(hasAnyPermission) => (
        <GenericItemsView
          fetchData={handleFetch}
          items={items}
          isLoading={isLoading}
          isAllDataLoaded={isAllDataLoaded}
          dataCount={itemsCount}
          isSelectDisabled={() => !hasAnyPermission}
          selectedIds={selectedIds}
          onSelectedItemChanged={setSelectedIds}
          noResultsContent={
            <SendsNoResults createButton={<CreateCampaignSendButton id={id} disabled={isSendDisabled} />} />
          }
          permissions={[RolePermissions.CampaignsManage]}
          columnOptions={columnOptions}
          defaultSortingColumnName={Columns.ScheduledTime}
          listViewRtnEvents={rtnEvents}
        />
      )}
    />
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState, ownProps: SendsProps) => {
  const base = campaignsStateSelector(state).base;

  return {
    id: ownProps.campaignId || base.entityState.entityId,
    items: base.sends.overview.items,
    itemsCount: base.sends.overview.itemsCount,
    isLoading: base.sends.overview.isLoading,
    isAllDataLoaded: base.sends.overview.areAllLoaded,
    selectedIds: base.sends.overview.selected,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  fetchCampaignSends: bindAction(fetchOverviewSends, dispatch),
  setSelectedIds: (ids: number[]) => dispatch(setSelected(ids)),
  completeCampaignRun: bindAction(completeCampaignSend, dispatch),
  cancelCampaignSend: bindAction(cancelCampaignSend, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(Sends);
export default ConnectedComponent;
